const normalizeSearchValue = (value) =>
  (value || "")
    .toString()
    .toLowerCase()
    .replace(/[_/.-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const getCleanText = (value) => String(value || "").replace(/\s+/g, " ").trim();

const isVisibleElement = (element) => {
  if (!element || !(element instanceof HTMLElement)) {
    return false;
  }

  const style = window.getComputedStyle(element);

  return style.display !== "none" && style.visibility !== "hidden" && !element.closest("[aria-hidden='true']");
};

const getSectionLabel = (element, root) => {
  const card = element.closest(".card");

  if (card) {
    const cardTitle = card.querySelector(".card-title, .block-title, .fs-15px.fw-bold, .title, h1, h2, h3, h4");
    const text = getCleanText(cardTitle?.textContent);
    if (text) {
      return text;
    }
  }

  const nearestHeading = element.closest("section, article, .nk-block, .nk-content-body")?.querySelector("h1, h2, h3, h4");
  const headingText = getCleanText(nearestHeading?.textContent);
  if (headingText) {
    return headingText;
  }

  return getCleanText(root?.querySelector("h1, h2, h3, h4")?.textContent) || "This Page";
};

const getPageSearchCandidates = (root) => {
  if (!root) {
    return [];
  }

  return [
    ...root.querySelectorAll(
      [
        ".nk-tb-item:not(.nk-tb-head)",
        ".card .card-inner",
        ".data-list-row",
        ".list-group-item",
        ".timeline-item",
        ".nk-data-item",
        "h1",
        "h2",
        "h3",
        "h4",
      ].join(", "),
    ),
  ];
};

export const buildCurrentPageSearchItems = () => {
  const root = document.querySelector("[data-global-search-root]");

  if (!root) {
    return [];
  }

  const seenEntries = new Set();

  return getPageSearchCandidates(root).reduce((items, element, index) => {
    if (!isVisibleElement(element)) {
      return items;
    }

    if (element.matches(".card .card-inner") && element.querySelector(".nk-tb-item:not(.nk-tb-head)")) {
      return items;
    }

    const text = getCleanText(element.innerText || element.textContent);

    if (text.length < 3) {
      return items;
    }

    const section = getSectionLabel(element, root);
    const entryKey = `${section}::${text}`;

    if (seenEntries.has(entryKey)) {
      return items;
    }

    seenEntries.add(entryKey);

    items.push({
      id: `page-search-${index}`,
      kind: "page",
      label: text.length > 90 ? `${text.slice(0, 87)}...` : text,
      meta: section,
      icon: "search",
      element,
      normalizedLabel: normalizeSearchValue(text),
      normalizedSearchText: normalizeSearchValue(`${section} ${text}`),
    });

    return items;
  }, []);
};

export const filterCurrentPageSearchItems = (query, searchItems) => {
  const normalizedQuery = normalizeSearchValue(query);

  if (!normalizedQuery) {
    return [];
  }

  const tokens = normalizedQuery.split(" ").filter(Boolean);

  return searchItems
    .map((item) => {
      let score = 0;

      for (const token of tokens) {
        if (item.normalizedLabel.startsWith(token)) {
          score += 120;
          continue;
        }

        if (item.normalizedLabel.includes(token)) {
          score += 80;
          continue;
        }

        if (item.normalizedSearchText.includes(token)) {
          score += 35;
          continue;
        }

        return null;
      }

      return {
        ...item,
        score,
      };
    })
    .filter(Boolean)
    .sort((itemA, itemB) => itemB.score - itemA.score || itemA.label.length - itemB.label.length)
    .slice(0, 8);
};
